import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Table,
	Space,
	Tag,
	Button,
	Modal,
	Tabs,
	Collapse,
	Form,
	Row,
	Col,
	Spin,
} from "antd";

//internal imports
import AppNotification from "../../../_components/AppNotification";
import { reportingDashboardActions } from "../../../_actions";
import ReportUpdate from "./ReportUpdate";

const DataTable = ({ filterValues, page, setPage }) => {
	const dispatch = useDispatch();

	const {
		reports,
		viewReportError,
		reportCount,
		isGetAllReportsLoading,
		currentRecord,
	} = useSelector((state) => state.reportsState);

	const { updatedReports, isGetAllUpdatedReportsLoading } = useSelector(
		(state) => state.updateReportState
	);

	const { getAllReports, getAllUpdatedReports, getSummaryCount } = reportingDashboardActions;

	const [editReport, setEditReport] = useState(false);
	const [updatePage, setUpdatePage] = useState(1);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [hasUpdate, setHasUpdate] = useState(false);
	const [modalData, setModalData] = useState([]);
	const [currentId, setCurrentId] = useState(0);
	const isMounted = useRef(false);

	// from antd
	const { TabPane } = Tabs;
	const { Panel } = Collapse;

	useEffect(() => {
		if (viewReportError) {
			AppNotification(
				viewReportError.errorMessage,
        "Unable to get Report record",
        "error"
			);
		}
		if (!isMounted.current) {
			dispatch(getAllReports(page));
			dispatch(getSummaryCount())
			isMounted.current = true;
		}
	});

	const showModal = (record) => {
		setIsModalVisible(true);
		setModalData(record);
		setCurrentId(record?.id);
		dispatch(getAllUpdatedReports(updatePage, record?.caseId));

		// api call to get updated reports
	};

	const handleOk = () => {
		setIsModalVisible(false);
		cancelEdit();
	};

	const renderStatus = (status) => {
		switch (status) {
      case "PENDING":
				return (
					<Tag className="tag" color="orange">
						PENDING
					</Tag>
				);
      case "SUCCESSFUL":
				return (
					<Tag className="tag" color="green">
						SUCCESSFUL
					</Tag>
				);
      case "FAILED":
				return (
					<Tag className="tag" color="red">
						FAILED
					</Tag>
				);
			case 'UNPROCESSED':
				return (
					<Tag className="tag" color="geekblue">
						UNPROCESSED
					</Tag>
				);
			default:
				return;
		}
	};

	const cancelEdit = () => {
		setEditReport(false);
	};

	const columns = [
		{
      title: "Terminal ID",
      dataIndex: "terminalId",
      key: "terminalId",
		},
		{
      title: "Case ID",
      dataIndex: "caseId",
      key: "caseId",
		},
		{
      title: "Account Name",
      dataIndex: "merchantName",
      key: "merchantName",
		},
		{
      title: "Account Number",
      dataIndex: "bankAccNo",
      key: "bankAccNo",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		{
      title: "Bank Code",
      dataIndex: "bankCode",
      key: "bankCode",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		{
      title: "PTSP Name",
      dataIndex: "ptspCode",
      key: "ptspCode",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		{
      title: "Merchant Category Code",
      dataIndex: "merchantCategoryCode",
      key: "merchantCategoryCode",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		// {
		//   title: "Terminal Owner Code",
		//   dataIndex: "terminalOwnerCode",
		//   key: "terminalOwnerCode",
		//   render: (text) => <span className="center">{text ? text : "-"}</span>,
		// },
		{
      title: "State Code",
      dataIndex: "stateCode",
      key: "stateCode",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		{
      title: "Date Created",
      dataIndex: "createdDate",
      key: "createdDate",
			render: (text) => {
				return (
					<span>
            {new Date(text).toLocaleDateString()}{" "}
						{new Date(text).toLocaleTimeString()}
					</span>
				);
			},
		},
		{
      title: "Status",
      dataIndex: "status",
      key: "status",
			render: (text) => {
				let color = 'green';
				if (text === 'FAILED') {
					color = 'red';
				} else if (text === 'PENDING') {
					color = 'orange';
				} else if (text === 'UNPROCESSED') {
					color = 'geekblue';
				}
				return (
					<Tag className="tag" color={color}>
						{text}
					</Tag>
				);
			},
		},
		{
      title: "Status Message",
      dataIndex: "description",
      key: "description",
			render: (text) => (
        <span style={{ width: "10rem", display: "inline-block" }}>{text}</span>
			),
		},
		// {
		//   title: "Treated Update",
		//   dataIndex: "treatedUpdate",
		//   key: "treatedUpdate",
		//   render: (text) => <span className="center">{text}</span>,
		// },
		{
      title: "Pending Update",
      dataIndex: "hasUpdate",
      key: "hasUpdate",
			render: (text, record) => {
				const { hasUpdate } = record;
				const { treatedUpdate } = record;
				return (
					<span className="center">
            {treatedUpdate < hasUpdate ? "Yes" : "No"}
					</span>
				);
			},
		},
		{
      title: "Last Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (text) => <span className="center">{text ? text : "-"}</span>,
		},
		{
      title: "",
      key: "action",
			render: (text, record) => (
				<Space size="middle">
					<Button type="primary" onClick={() => showModal(record)}>
						View
					</Button>
				</Space>
			),
		},
	];

	const pagination = {
		total: reportCount,
		pageSize: 10,
		showSizeChanger: false,
		current: page,
		showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
		onChange: (page) => {
			setPage(page);
		},
	};

	return (
		<div>
			<Modal
				title="REPORT DETAILS"
				open={isModalVisible}
				onCancel={handleOk}
        scroll={{ x: "max-content" }}
        width={"81%"}
				style={{
          maxWidth: "100%",
          width: "100%",
				}}
				footer={null}
			>
				<Tabs
					defaultActiveKey="1"
					centered={true}
          tabBarStyle={{ textAlign: "center", margin: "10px" }}
				>
					{/* REQUEST DETAILS TAB */}
					<TabPane tab="Report Details" key="1">
						{editReport && (
							<ReportUpdate
								cancelEdit={cancelEdit}
								currentId={currentId}
								setIsModalVisible={setIsModalVisible}
								page={page}
							/>
						)}
						{!editReport && (
							<>
								<Row
									type="flex"
									justify="space-between"
									style={{
                    marginBottom: "1rem",
                    padding: "0 2rem",
									}}
								>
									<Row gutter={32} type="flex">
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Merchant Name </label>
												<span className="view-text">
													{modalData.merchantName === null
                            ? "Not entered"
														: modalData.merchantName}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Merchant ID </label>
												<span className="view-text">
													{modalData.merchantId === null
                            ? "Not entered"
														: modalData.merchantId}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Merchant Category Code
												</label>
												<span className="view-text">
													{modalData.merchantCategoryCode === null
                            ? "Not entered"
														: modalData.merchantCategoryCode}
												</span>
											</Form.Item>
										</Col>

										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Merchant Physical Address
												</label>
												<span className="view-text">
													{modalData.merchantPhysicalAddr === null
                            ? "Not entered"
														: modalData.merchantPhysicalAddr}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Merchant Address LGA Code
												</label>
												<span className="view-text">
													{modalData.merchantAddressLgaCode === null
                            ? "Not entered"
														: modalData.merchantAddressLgaCode}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">GPS Information</label>
												<span className="view-text">
													{modalData.gpsInfo === null
                            ? "Not entered"
														: modalData.gpsInfo}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Case ID</label>
												<span className="view-text">
													{modalData.caseId === null
                            ? "Not entered"
														: modalData.caseId}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Registration Reference
												</label>
												<span className="view-text">
													{modalData.registrationReference === null
                            ? "Not entered"
														: modalData.registrationReference}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Registration Type</label>
												<span className="view-text">
													{modalData.registrationType === null
                            ? "Not entered"
														: modalData.registrationType}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Status</label>
												<span className="view-text">
													{modalData.status === null
                            ? "Not entered"
														: renderStatus(modalData.status)}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Remote Status</label>
												<span className="view-text">
													{modalData.remoteStatus === null
                            ? "Not entered"
														: modalData.remoteStatus}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Date Created</label>
												<span className="view-text">
													{modalData.createdDate === null
                            ? "Not entered"
														: modalData.createdDate}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">State Code</label>
												<span className="view-text">
													{modalData.stateCode === null
                            ? "Not entered"
														: modalData.stateCode}
												</span>
											</Form.Item>
										</Col>

										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Contact Name </label>
												<span className="view-text">
													{modalData.contactName === null
                            ? "Not entered"
														: modalData.contactName}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Contact Title </label>
												<span className="view-text">
													{modalData.contactTitle === null
                            ? "Not entered"
														: modalData.contactTitle}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Phone Number</label>
												<span className="view-text">
													{modalData.mobilePhone === null
                            ? "Not entered"
														: modalData.mobilePhone}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Email Address</label>
												<span className="view-text">
                          {modalData.email === ""
                            ? "Not entered"
														: modalData.email}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Terminal Type</label>
												<span className="view-text">
													{modalData.terminalType === null
                            ? "Not entered"
														: modalData.terminalType}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Terminal ID</label>
												<span className="view-text">
													{modalData.terminalId === null
                            ? "Not entered"
														: modalData.terminalId}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Terminal Owner Code
												</label>
												<span className="view-text">
													{modalData.terminalOwnerCode === null
                            ? "Not entered"
														: modalData.terminalOwnerCode}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Terminal Model Description
												</label>
												<span className="view-text">
													{modalData.terminalModelDescription === null
                            ? "Not entered"
														: modalData.terminalModelDescription}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Bank Account Number
												</label>
												<span className="view-text">
													{modalData.bankAccNo === null
                            ? "Not entered"
														: modalData.bankAccNo}
												</span>
											</Form.Item>
										</Col>

										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Bank Code</label>
												<span className="view-text">
													{modalData.bankCode === null
                            ? "Not entered"
														: modalData.bankCode}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Merchant Acquirer ID
												</label>
												<span className="view-text">
													{modalData.merchantAcquirerId === null
                            ? "Not entered"
														: modalData.merchantAcquirerId}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Visa Acquirer Id Number
												</label>
												<span className="view-text">
													{modalData.visaAcquirerIdNumber === null
                            ? "Not entered"
														: modalData.visaAcquirerIdNumber}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Mastercard Acquirer Id Number
												</label>
												<span className="view-text">
													{modalData.mastercardAcquirerIdNumber === null
                            ? "Not entered"
														: modalData.mastercardAcquirerIdNumber}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Verve Acquirer Id Number
												</label>
												<span className="view-text">
													{modalData.verveAcquirerIdNumber === null
                            ? "Not entered"
														: modalData.verveAcquirerIdNumber}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">
													Business Occupation Code
												</label>
												<span className="view-text">
													{modalData.businessOccupationCode === null
                            ? "Not entered"
														: modalData.businessOccupationCode}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">PTSP Code</label>
												<span className="view-text">
													{modalData.ptspCode === null
                            ? "Not entered"
														: modalData.ptspCode}
												</span>
											</Form.Item>
										</Col>
										<Col sm={12} md={8} lg={8} xl={8} xxl={8}>
											<Form.Item>
												<label className="view-label">Last Updated By</label>
												<span className="view-text">
													{modalData.updatedBy === null
                            ? "Not entered"
														: modalData.updatedBy}
												</span>
											</Form.Item>
										</Col>
									</Row>
								</Row>
								<div
									style={{
                    textAlign: "right",
                    marginBottom: "5rem",
                    marginTop: "2rem",
									}}
								>
									<Button
										disabled={modalData?.hasUpdate === 0}
										type="primary"
										onClick={() => setEditReport(true)}
                    style={{ width: "5rem" }}
									>
										Edit
									</Button>
								</div>
							</>
						)}
					</TabPane>
					{modalData?.hasUpdate !== 0 && (
						<TabPane tab="Updates" key="2">
							{modalData.hasUpdate !== 0 ? (
								<>
									<Spin
										spinning={isGetAllUpdatedReportsLoading}
										tip="Loading Updates..."
									>
                    <Collapse defaultActiveKey={["1"]} key="1">
											{updatedReports?.map((data, index) => (
												<Panel header={data.caseId} key={index + 1}>
													<div>
														<h3 className="segment">Report Details</h3>
														<div className="segment-content">
															<pre>{JSON.stringify(data, null, 2)}</pre>
														</div>
													</div>
												</Panel>
											))}
										</Collapse>
									</Spin>
								</>
							) : (
								<div className="empty-tab">No Update found</div>
							)}
						</TabPane>
					)}
				</Tabs>
			</Modal>
			<Table
				rowKey={(record) => record.id}
				columns={columns}
				dataSource={reports}
				pagination={pagination}
				loading={isGetAllReportsLoading}
        scroll={{ x: "max-content" }}
			/>
		</div>
	);
};
export default DataTable;
